/**
 * OIDC Client-Side Debugging Utility
 *
 * This utility helps troubleshoot OIDC authentication issues by collecting
 * client-side information and testing connectivity to OIDC endpoints.
 */

import { hasTokenParameter, runLegacyEndpointTests } from './LegacyKeyHandler';

export interface OIDCDebugInfo {
  timestamp: string;
  url: string;
  hostname: string;
  isPreviewEnvironment: boolean;
  userAgent: string;
  cookies: Record<string, string>;
  sessionStorage: Record<string, string>;
  localStorage: Record<string, string>;
  networkInfo?: {
    effectiveType?: string;
    downlink?: number;
    rtt?: number;
  };
  referrer: string;
  origin: string;
  protocol: string;
}

export interface OIDCEndpointTest {
  endpoint: string;
  method: string;
  status: number;
  statusText: string;
  headers: Record<string, string>;
  responseTime: number;
  error?: string;
  corsEnabled: boolean;
  redirected: boolean;
  finalUrl: string;
}

export interface OIDCDebugReport {
  debugInfo: OIDCDebugInfo;
  endpointTests: OIDCEndpointTest[];
  recommendations: string[];
  severity: 'info' | 'warning' | 'error';
}

/**
 * Collects comprehensive client-side debug information
 */
export function collectDebugInfo(): OIDCDebugInfo {
  const url = window.location.href;
  const hostname = window.location.hostname;
  const isPreviewEnvironment = hostname.includes('preview.cms-');

  // Collect cookies (only names for security)
  const cookies: Record<string, string> = {};
  if (document.cookie) {
    document.cookie.split(';').forEach(cookie => {
      const [name, value] = cookie.trim().split('=');
      // Only log cookie names and first few characters for security
      cookies[name] = value ? `${value.substring(0, 4)}...` : 'empty';
    });
  }

  // Collect storage (safely)
  const sessionStorage: Record<string, string> = {};
  const localStorage: Record<string, string> = {};

  try {
    for (let i = 0; i < window.sessionStorage.length; i++) {
      const key = window.sessionStorage.key(i);
      if (key) {
        const value = window.sessionStorage.getItem(key);
        sessionStorage[key] = value ? `${value.substring(0, 7)}...` : 'empty';
      }
    }
  } catch {
    sessionStorage['error'] = 'Access denied';
  }

  try {
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (key) {
        const value = window.localStorage.getItem(key);
        localStorage[key] = value ? `${value.substring(0, 8)}...` : 'empty';
      }
    }
  } catch {
    localStorage['error'] = 'Access denied';
  }

  // Network information (if available)
  let networkInfo: OIDCDebugInfo['networkInfo'];
  if ('connection' in navigator) {
    const connection = (navigator as any).connection;
    networkInfo = {
      effectiveType: connection.effectiveType,
      downlink: connection.downlink,
      rtt: connection.rtt,
    };
  }

  return {
    timestamp: new Date().toISOString(),
    url,
    hostname,
    isPreviewEnvironment,
    userAgent: navigator.userAgent,
    cookies,
    sessionStorage,
    localStorage,
    networkInfo,
    referrer: document.referrer,
    origin: window.location.origin,
    protocol: window.location.protocol,
  };
}

/**
 * Tests an OIDC endpoint and collects response information
 */
export async function testOIDCEndpoint(
  endpoint: string,
  method: 'GET' | 'POST' | 'OPTIONS' = 'GET',
  includeCredentials = true,
): Promise<OIDCEndpointTest> {
  const startTime = performance.now();

  try {
    const requestInit: RequestInit = {
      method,
      credentials: includeCredentials ? 'include' : 'omit',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    };

    // Add body for POST requests
    if (method === 'POST') {
      requestInit.body = JSON.stringify({
        debug: true,
        timestamp: new Date().toISOString(),
      });
    }

    const response = await fetch(endpoint, requestInit);
    const endTime = performance.now();

    // Handle case where fetch might return undefined (shouldn't happen but defensive programming)
    if (!response) {
      return {
        endpoint,
        method,
        status: 0,
        statusText: 'Network Error',
        headers: {},
        responseTime: endTime - startTime,
        error: 'Fetch returned undefined response',
        corsEnabled: false,
        redirected: false,
        finalUrl: endpoint,
      };
    }

    // Check if request was redirected
    const redirected = response.redirected;
    const finalUrl = response.url;

    // Collect response headers
    const headers: Record<string, string> = {};
    response.headers.forEach((value, key) => {
      headers[key] = value;
    });

    // Check for CORS headers
    const corsEnabled = response.headers.has('access-control-allow-origin');

    return {
      endpoint,
      method,
      status: response.status,
      statusText: response.statusText,
      headers,
      responseTime: endTime - startTime,
      corsEnabled,
      redirected,
      finalUrl,
    };
  } catch (error) {
    const endTime = performance.now();
    return {
      endpoint,
      method,
      status: 0,
      statusText: 'Network Error',
      headers: {},
      responseTime: endTime - startTime,
      error: error instanceof Error ? error.message : String(error),
      corsEnabled: false,
      redirected: false,
      finalUrl: endpoint,
    };
  }
}

/**
 * Generates recommendations based on debug information and test results
 */
export function generateRecommendations(
  debugInfo: OIDCDebugInfo,
  endpointTests: OIDCEndpointTest[],
): { recommendations: string[]; severity: 'info' | 'warning' | 'error' } {
  const recommendations: string[] = [];
  let severity: 'info' | 'warning' | 'error' = 'info';

  // Check environment
  if (!debugInfo.isPreviewEnvironment) {
    recommendations.push(
      '⚠️ Not in preview environment - OIDC authentication may not be required',
    );
    severity = 'warning';
  }

  // Check protocol
  if (debugInfo.protocol !== 'https:') {
    recommendations.push(
      '🔒 Non-HTTPS environment detected - OIDC cookies may not work properly',
    );
    severity = 'error';
  }

  // Analyze endpoint tests
  const oidcEndpointTest = endpointTests.find(test =>
    test.endpoint.includes('/internal/jwt'),
  );

  const keysEndpointTest = endpointTests.find(test =>
    test.endpoint.includes('/kontent-ai/keys-preview'),
  );

  // Provide context about authentication flow
  const hasTokenParam = hasTokenParameter(debugInfo.url);

  if (hasTokenParam) {
    recommendations.push(
      '🔑 Token parameter detected - Using token-based authentication flow',
    );
  } else {
    recommendations.push(
      '🔐 Using ALB OIDC authentication flow (no token parameter)',
    );
  }

  if (oidcEndpointTest) {
    if (oidcEndpointTest.status === 302) {
      recommendations.push(
        '✅ 302 redirect to OIDC login - Expected behavior for unauthenticated requests',
      );
      recommendations.push(
        '🔑 To test authenticated flow, log in first then retry',
      );
    } else if (oidcEndpointTest.status === 401) {
      recommendations.push(
        '🔐 401 Unauthorized - Check if ALB OIDC headers are present',
      );
      recommendations.push('📋 Verify Lambda authorizer configuration');
      severity = 'warning';
    } else if (oidcEndpointTest.status === 403) {
      recommendations.push(
        '🚫 403 Forbidden - API Gateway may be rejecting the request',
      );
      recommendations.push(
        '🔍 Check API Gateway resource policies and CORS configuration',
      );
      severity = 'warning';
    } else if (oidcEndpointTest.status === 0) {
      recommendations.push(
        '🌐 Network error - Check connectivity to API Gateway',
      );
      recommendations.push('🔧 Verify DNS resolution and firewall settings');
      severity = 'error';
    }

    if (!oidcEndpointTest.corsEnabled && oidcEndpointTest.status !== 302) {
      recommendations.push(
        '❌ CORS headers missing - May cause browser to block requests',
      );
      severity = severity === 'error' ? 'error' : 'warning';
    }

    if (oidcEndpointTest.redirected) {
      recommendations.push(
        '🔄 Request was redirected - Check final URL for OIDC login page',
      );
    }
  }

  // Analyze keys-preview endpoint if tested
  if (keysEndpointTest) {
    if (keysEndpointTest.status === 200) {
      recommendations.push(
        '✅ Keys endpoint accessible - Token-based authentication working',
      );
    } else if (keysEndpointTest.status === 401) {
      recommendations.push(
        '🔐 Keys endpoint unauthorized - Token may be invalid or expired',
      );
      severity = severity === 'error' ? 'error' : 'warning';
    } else if (keysEndpointTest.status === 403) {
      recommendations.push(
        '🚫 Keys endpoint forbidden - Check token permissions',
      );
      severity = severity === 'error' ? 'error' : 'warning';
    }
  }

  // Check cookies
  const hasOIDCCookies = Object.keys(debugInfo.cookies).some(
    cookie =>
      cookie.toLowerCase().includes('oidc') ||
      cookie.toLowerCase().includes('alb') ||
      cookie.toLowerCase().includes('auth'),
  );

  if (debugInfo.isPreviewEnvironment && !hasOIDCCookies) {
    recommendations.push(
      '🍪 No OIDC/ALB cookies found - User may not be authenticated',
    );
    recommendations.push('👤 Try logging in to establish OIDC session');
  }

  // Network recommendations
  if (
    debugInfo.networkInfo?.effectiveType &&
    debugInfo.networkInfo.effectiveType.includes('slow')
  ) {
    recommendations.push(
      '🐌 Slow network detected - OIDC redirects may timeout',
    );
  }

  return { recommendations, severity };
}

/**
 * Runs a comprehensive OIDC debug check
 */
export async function runOIDCDebugCheck(): Promise<OIDCDebugReport> {
  console.log('🔍 Starting OIDC debug check...');

  // Collect debug information
  const debugInfo = collectDebugInfo();

  // Use the legacy key handler to determine and test endpoints
  const endpointTests = await runLegacyEndpointTests(
    debugInfo.hostname,
    debugInfo.url,
  );

  // Generate recommendations
  const { recommendations, severity } = generateRecommendations(
    debugInfo,
    endpointTests,
  );

  const report: OIDCDebugReport = {
    debugInfo,
    endpointTests,
    recommendations,
    severity,
  };

  console.log('✅ OIDC debug check completed', report);
  return report;
}

/**
 * Formats debug report for display or logging
 */
export function formatDebugReport(report: OIDCDebugReport): string {
  const { debugInfo, endpointTests, recommendations, severity } = report;

  const severityIcon =
    severity === 'error' ? '🔴' : severity === 'warning' ? '🟡' : '🟢';

  let output = `${severityIcon} OIDC Debug Report - ${severity.toUpperCase()}\n`;
  output += `Generated: ${debugInfo.timestamp}\n\n`;

  output += `🌐 Environment Information:\n`;
  output += `  URL: ${debugInfo.url}\n`;
  output += `  Hostname: ${debugInfo.hostname}\n`;
  output += `  Preview Environment: ${debugInfo.isPreviewEnvironment ? 'Yes' : 'No'}\n`;
  output += `  Protocol: ${debugInfo.protocol}\n`;
  output += `  Origin: ${debugInfo.origin}\n`;
  output += `  Referrer: ${debugInfo.referrer || 'None'}\n\n`;

  output += `🍪 Cookies (${Object.keys(debugInfo.cookies).length}):\n`;
  Object.entries(debugInfo.cookies).forEach(([name, value]) => {
    output += `  ${name}: ${value}\n`;
  });
  output += '\n';

  output += `🧪 Endpoint Tests:\n`;
  endpointTests.forEach(test => {
    const statusIcon =
      test.status >= 200 && test.status < 300
        ? '✅'
        : test.status >= 300 && test.status < 400
          ? '🔄'
          : test.status >= 400
            ? '❌'
            : '⚠️';
    output += `  ${statusIcon} ${test.method} ${test.endpoint}\n`;
    output += `     Status: ${test.status} ${test.statusText}\n`;
    output += `     Response Time: ${test.responseTime.toFixed(2)}ms\n`;
    output += `     CORS Enabled: ${test.corsEnabled ? 'Yes' : 'No'}\n`;
    output += `     Redirected: ${test.redirected ? 'Yes' : 'No'}\n`;
    if (test.redirected) {
      output += `     Final URL: ${test.finalUrl}\n`;
    }
    if (test.error) {
      output += `     Error: ${test.error}\n`;
    }
    output += '\n';
  });

  output += `💡 Recommendations:\n`;
  recommendations.forEach(rec => {
    output += `  ${rec}\n`;
  });

  return output;
}

/**
 * Utility function to debug OIDC issues - can be called from browser console
 */
export async function debugOIDC(): Promise<void> {
  try {
    const report = await runOIDCDebugCheck();
    const formatted = formatDebugReport(report);
    console.log(formatted);

    // Also make the report available as a global variable for further inspection
    (window as any).oidcDebugReport = report;
    console.log('📋 Full report available as window.oidcDebugReport');

    // Offer to copy to clipboard if available
    if (navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(formatted);
        console.log('📋 Debug report copied to clipboard');
      } catch {
        console.log(
          'ℹ️ Could not copy to clipboard, but report is logged above',
        );
      }
    }
  } catch (error) {
    console.error('❌ OIDC debug check failed:', error);
  }
}
